// src/rms/data/exportService.js
// ============================================================================
// Operator data export
// ============================================================================
// "Do I own my data?" almost always means "can I leave without losing
// everything?" This module is the answer to that question, so it has to be
// complete rather than convenient: every operator-scoped table, exported
// through the same adapter the app itself uses, so it behaves identically on
// the local backend and on Supabase.
//
// Two formats, because they serve different people:
//
//   JSON — the whole business in one file, structure intact, values raw. This
//          is the migrate-elsewhere artefact and must round-trip, so nothing
//          here is formatted for display.
//   CSV  — one table at a time, opened in a spreadsheet by an operator
//          checking their invoices. Formatted for a human, and never
//          re-imported.
//
// Deliberately excluded: rms_ad_impressions and rms_ad_clicks. That is platform
// telemetry about advertisers, not the operator's business record — it is also
// the bulky part, and including it would bury the 30 bookings someone actually
// came for under 1,300 ad events.
//
// No new dependencies: Blob + object URL only. A zip library for a once-a-year
// action is not worth the bundle.
// ============================================================================

import db from "./index";
import { signedUrlFor } from "./documentStorage";
import { isSuccess } from "../utils/response";

/**
 * Everything an operator owns, in the order a human would want to read it:
 * the business, then what it rents, then who rents it, then the money.
 *
 * `scope` says which column carries the tenant key. Every operator-scoped
 * rms_* table has `operator_id NOT NULL ... ON DELETE CASCADE` (verified across
 * all 12 migrations), so this list needs no per-table special cases.
 */
export const EXPORT_COLLECTIONS = [
  { key: "operators", label: "Business", scope: "self" },
  { key: "members", label: "Staff" },
  { key: "locations", label: "Locations" },

  { key: "assetGroups", label: "Inventory groups" },
  { key: "assets", label: "Inventory" },
  { key: "assetNotes", label: "Inventory notes" },
  { key: "assetDocuments", label: "Inventory documents" },

  { key: "customers", label: "Customers" },
  { key: "customerDocuments", label: "Customer documents" },
  { key: "certifications", label: "Customer certifications" },

  { key: "reservations", label: "Reservations" },
  { key: "reservationAddons", label: "Reservation add-ons" },
  { key: "reservationAnswers", label: "Reservation answers" },
  { key: "agreements", label: "Agreements" },
  { key: "usageReadings", label: "Usage readings" },
  { key: "conditionReports", label: "Condition reports" },
  { key: "deliveries", label: "Deliveries" },

  { key: "occupancies", label: "Occupancies" },
  { key: "accessCredentials", label: "Access credentials" },
  { key: "accessEvents", label: "Access events" },

  { key: "invoices", label: "Invoices" },
  { key: "payments", label: "Payments" },
  { key: "subscriptions", label: "Subscriptions" },

  { key: "addons", label: "Add-ons" },
  { key: "rateRules", label: "Rate rules" },
  { key: "usageFeeSchedules", label: "Usage fee schedules" },
  { key: "cancellationPolicies", label: "Cancellation policies" },
  { key: "checkoutQuestions", label: "Checkout questions" },
  { key: "agreementTemplates", label: "Agreement templates" },
  { key: "agreementDocuments", label: "Signed agreements" },
  { key: "availabilityBlocks", label: "Availability blocks" },
  { key: "operatingHours", label: "Opening hours" },
  { key: "hoursExceptions", label: "Closures and short days" },
];

/** Archive format version. Bump when the shape changes, never silently. */
const FORMAT_VERSION = 1;

// ----------------------------------------------------------------------------
// Collection
// ----------------------------------------------------------------------------

/**
 * Reads every operator-scoped collection.
 *
 * Failures are collected rather than thrown: a partial export with an explicit
 * list of what could not be read is far more useful to someone leaving than no
 * export at all, and silently omitting a table would be the worst of the three.
 *
 * @returns {Promise<{archive: object, errors: Array<{collection: string, error: string}>}>}
 */
export async function collectOperatorData(operatorId) {
  if (!operatorId) throw new Error("An operator id is required to export data.");

  const collections = {};
  const counts = {};
  const errors = [];

  for (const entry of EXPORT_COLLECTIONS) {
    const where = entry.scope === "self" ? { id: operatorId } : { operator_id: operatorId };

    // Sequential rather than parallel. An export is rare and not latency
    // sensitive, and firing ~28 concurrent queries at a free-tier Postgres
    // connection pool is a good way to make it fail for everyone else.
    const result = await db.list(entry.key, where, { orderBy: "created_at" });

    if (!isSuccess(result)) {
      errors.push({ collection: entry.key, error: result?.error || "Unknown error" });
      collections[entry.key] = [];
      counts[entry.key] = 0;
      continue;
    }

    const rows = result.data || [];
    collections[entry.key] = rows;
    counts[entry.key] = rows.length;
  }

  const operator = collections.operators?.[0] || null;

  // A storage_path is meaningless outside this system. Someone taking their
  // data elsewhere needs to be able to actually fetch the scans, so each
  // document gets a signed URL alongside the path — with the expiry stated in
  // the archive, because a link that quietly stopped working would be worse
  // than no link at all.
  const documents = collections.customerDocuments || [];
  let scanUrlsExpireAt = null;

  if (documents.some((d) => d.storage_path)) {
    const expiresIn = 7 * 24 * 60 * 60;
    scanUrlsExpireAt = new Date(Date.now() + expiresIn * 1000).toISOString();

    for (const doc of documents) {
      if (!doc.storage_path) continue;
      const link = await signedUrlFor(doc.storage_path, expiresIn);
      // A scan that cannot be signed is reported, not silently dropped.
      if (link.success) doc.scan_url = link.data;
      else errors.push({ collection: "customerDocuments", error: `${doc.file_name || doc.storage_path}: ${link.error}` });
    }
  }

  return {
    archive: {
      format: "reservezoo.operator-export",
      format_version: FORMAT_VERSION,
      generated_at: new Date().toISOString(),
      source: db.name,
      operator_id: operatorId,
      business_name: operator?.business_name || null,
      vertical_key: operator?.vertical_key || null,
      counts,
      // Ad delivery is intentionally absent — see the header note.
      excluded: ["adImpressions", "adClicks"],
      scan_urls_expire_at: scanUrlsExpireAt,
      collections,
    },
    errors,
  };
}

// ----------------------------------------------------------------------------
// CSV rendering
// ----------------------------------------------------------------------------

/** Money is stored as integer cents everywhere; a spreadsheet wants decimals. */
const isMoneyColumn = (key) => /(^|_)cents$/.test(key);

function renderCell(key, value) {
  if (value === null || value === undefined) return "";
  if (isMoneyColumn(key) && typeof value === "number") return (value / 100).toFixed(2);
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/**
 * Escapes one field for CSV.
 *
 * The leading-apostrophe guard is not cosmetic: a customer named "=cmd" or a
 * note starting with "+" is interpreted as a formula by Excel and Sheets. We
 * write operator-entered free text into these files, so the export is exactly
 * the injection path that has to be closed.
 */
function escapeCsv(text) {
  let out = text;
  if (/^[=+\-@\t\r]/.test(out)) out = `'${out}`;
  if (/[",\n\r]/.test(out)) out = `"${out.replace(/"/g, '""')}"`;
  return out;
}

/**
 * Renders rows to CSV. The header is the union of every key present, so a row
 * that carries an optional column does not silently lose it.
 */
export function toCsv(rows) {
  if (!rows?.length) return "";

  const columns = [];
  const seen = new Set();
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (!seen.has(key)) {
        seen.add(key);
        columns.push(key);
      }
    });
  });

  const lines = [columns.map(escapeCsv).join(",")];
  rows.forEach((row) => {
    lines.push(columns.map((key) => escapeCsv(renderCell(key, row[key]))).join(","));
  });

  // CRLF: Excel is still the most common destination, and it is the format the
  // RFC specifies.
  return `${lines.join("\r\n")}\r\n`;
}

// ----------------------------------------------------------------------------
// Delivery
// ----------------------------------------------------------------------------

function slugify(text, fallback) {
  const slug = String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || fallback;
}

/** Triggers a browser download. Revokes the object URL so the blob is freed. */
export function downloadFile(filename, mimeType, contents) {
  const blob = new Blob([contents], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Safari needs the URL to survive the click; a tick is enough.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const stamp = () => new Date().toISOString().slice(0, 10);

/**
 * Downloads the complete archive as JSON.
 * @returns {Promise<{counts: object, errors: Array, filename: string}>}
 */
export async function exportOperatorJson(operator) {
  const { archive, errors } = await collectOperatorData(operator.id);
  const filename = `reservezoo-${slugify(operator.business_name, "export")}-${stamp()}.json`;

  downloadFile(filename, "application/json", JSON.stringify(archive, null, 2));

  return { counts: archive.counts, errors, filename };
}

/**
 * Downloads one collection as CSV.
 * @returns {Promise<{rowCount: number, filename: string|null}>}
 */
export async function exportOperatorCsv(operator, collectionKey) {
  const entry = EXPORT_COLLECTIONS.find((c) => c.key === collectionKey);
  if (!entry) throw new Error(`"${collectionKey}" is not an exportable collection.`);

  const where = entry.scope === "self" ? { id: operator.id } : { operator_id: operator.id };
  const result = await db.list(entry.key, where, { orderBy: "created_at" });

  if (!isSuccess(result)) throw new Error(result?.error || `Could not read ${entry.label}.`);

  const rows = result.data || [];
  if (!rows.length) return { rowCount: 0, filename: null };

  const filename = `reservezoo-${slugify(operator.business_name, "export")}-${slugify(entry.label, entry.key)}-${stamp()}.csv`;
  downloadFile(filename, "text/csv", toCsv(rows));

  return { rowCount: rows.length, filename };
}

export default {
  EXPORT_COLLECTIONS,
  collectOperatorData,
  exportOperatorJson,
  exportOperatorCsv,
  toCsv,
  downloadFile,
};
